import { DEFAULT_API_RECORDS_LIMIT } from "../../Constants";

const endpoints = {
  // Authentication
  logout: "auth/logout",
  verifyJwt: "auth/verify-token",
  newOffers: `offers/get_pending`,
  suppliers: ({
    page = 1,
    pageSize = DEFAULT_API_RECORDS_LIMIT,
    searchValue = "",
    categoryIds = null,
    zoneIds = null,
  }) => {
    const baseUrl = "suppliers/search?";
    const params = new URLSearchParams();

    if (searchValue) {
      params.append("search_term", searchValue);
    }

    if (categoryIds.length) {
      const categoryIdsArray = categoryIds
        .map((id) => parseInt(id, 10))
        .filter(Number.isInteger);
      categoryIdsArray.forEach((id) => params.append("category_ids", id));
      //params.append('category_ids', categoryIds.join(','));
    }

    if (zoneIds.length) {
      const zoneIdsArray = zoneIds
        .map((id) => parseInt(id, 10))
        .filter(Number.isInteger);
      zoneIdsArray.forEach((id) => params.append("zone_ids", id));
      //params.append('zone_ids', zoneIds.join(','));
    }

    params.append("page", page);
    params.append("page_size", pageSize);

    return baseUrl + params.toString();
  },
  getSupplierProfile: (id) =>
    `community/supplier-management/suppliers/${id}/profile`,
  invitationLink: `/suppliers/get_invitation`,
  getZones: `zones/get_all`,
  getAllCategories: `categories/get_all`,
  communityStats: `community/stats`,
  cancelSupplierRequest: (id) =>
    `community/supplier-management/invites/${id}/cancel`,
  removeSupplier: (id) => `community/supplier-management/invites/${id}/remove`,
  getSupplierManagementStats: `community/supplier-management/stats`,
  getSupplierManagementMembers: (params) => {
    const {
      type = "invites",
      searchValue = "",
      category_ids = [],
      pageNumber = 1,
      pageSize = DEFAULT_API_RECORDS_LIMIT,
      sort_order,
    } = params;
    const baseUrl = `community/supplier-management/${type}`;
    const queryParams = [
      ...category_ids.map((id) => `category_ids=${id}`),
      `page=${pageNumber}`,
      `page_size=${pageSize}`,
      ...(searchValue ? [`search=${encodeURIComponent(searchValue)}`] : []),
      sort_order && `sort_order=${sort_order}`,
    ];
    return `${baseUrl}?${queryParams.join("&")}`;
  },
  removeCommunityMember: (id) => `community/members/${id}/remove`,
  approveCommunityMember: (id) => `community/members/${id}/approve`,
  rejectCommunityMember: (id) => `community/members/${id}/reject`,
  communityMembers: ({
    search = "",
    pageNumber = 1,
    pageSize = DEFAULT_API_RECORDS_LIMIT,
    status = "PENDING",
    sortOrder = "asc",
    sortBy = "join_date",
  }) => {
    const baseUrl = `community/get_members?`;

    const queryParams = [
      status && `status=${encodeURIComponent(status)}`,
      sortOrder && `sort_order=${encodeURIComponent(sortOrder)}`,
      sortBy && `sort_by=${encodeURIComponent(sortBy)}`,
      `page=${pageNumber}`,
      `page_size=${pageSize}`,
      search && `search=${encodeURIComponent(search)}`,
    ].filter(Boolean); // Remove any empty strings
    console.log("qery", queryParams.join("&"));
    return baseUrl + queryParams.join("&");
  },
  communityManagmentStats: `supplier/community-management/stats`,
  communityManagementMembers: (params) => {
    const { type, pageNumber, pageSize, sort_order } = params;
    const baseUrl = `supplier/community-management/${type}?`;

    const queryParams = [
      `page=${pageNumber}`,
      `page_size=${pageSize}`,
      sort_order && `sort_order=${sort_order}`,
    ].filter(Boolean);

    return baseUrl + queryParams.join("&");
  },
  communityManagementPauseCommunity: (id) =>
    `supplier/community-management/${id}/pause`,
  communityManagementRemoveCommunity: (id) =>
    `supplier/community-management/communities/${id}/remove`,
  communityManagementJoinCommunity: (id) =>
    `supplier/community-management/join-requests/${id}/accept`,
  communityManagementIgnoreCommunity: (id) =>
    `supplier/community-management/join-requests/${id}/ignore`,
  communitySettingsTags: "community/settings/tags/predefined",
  communitySettings: "community/settings",
  communitySettingsImg: "community/settings/image",
  updateCommunityInfo: "community/settings/community-info",
  updateCommunityManagerInfo: "community/settings/manager-contact",
  updateCommunityTOSSupplier: "community/settings/tos/supplier",
  updateCommunityTOSMember: "community/settings/tos/member",
  updateCommunityPlatforms: "/community/settings/platforms",
  joinCommunityRequest: (id) =>
    `supplier/community-management/${id}/initial_join_request`,
  // Withdrawing an offer that is still awaiting the CM's approval. NOT the same
  // as /remove: the supplier is INITIAL_OFFER_PENDING, a state /remove rejects
  // outright ("supplier must be active or paused to leave").
  cancelJoinCommunityRequest: (id) =>
    `supplier/community-management/${id}/cancel_initial_join_request`,
  getPendingJoinRequests: `supplier/community-management/pending-join-requests`,
  getCommunityRanges: `communities/get_members_ranges`,
  communities: ({
    page = 1,
    pageSize = 50,
    searchValue,
    membersRange,
    zoneIds = [],
  }) => {
    const baseUrl = "communities/search?";
    const params = new URLSearchParams();

    if (searchValue) {
      params.append("search_term", encodeURIComponent(searchValue));
    }

    if (membersRange) {
      if (membersRange.min !== undefined) {
        params.append("min_members", membersRange.min);
      }
      if (membersRange.max !== undefined) {
        params.append("max_members", membersRange.max);
      }
    }

    if (zoneIds.length) {
      zoneIds.forEach((zoneId) => params.append("zone_ids", zoneId));
    }

    params.append("page", page);
    params.append("page_size", pageSize);

    return baseUrl + params.toString();
  },
  acceptUpdateCommunityTOS: "community/settings/tos/supplier/approve",
  // Supplier Profile endpoints
  getSupplierProfilePricelists: (supplierId) =>
    `community/supplier-profile/${supplierId}/pricelists-details`,
  // `vertical` selects the catalog the CM is reviewing — omitted means
  // products, so existing callers are unaffected.
  getSupplierProfileProducts: (supplierId, vertical) =>
    `community/supplier-profile/${supplierId}/products-details` +
    (vertical === "restaurants" ? `?vertical=restaurants` : ""),
  acceptSupplierProductOffer: (supplierId) =>
    `community/supplier-profile/${supplierId}/accept-products`,
  // Approves the whole offer — services, store and menu — in one transactional
  // request. Replaces the old one-POST-per-vertical sequence.
  acceptSupplierOfferAll: (supplierId) =>
    `community/supplier-profile/${supplierId}/accept-all`,
  inviteSupplier: (supplierId) =>
    `community/supplier-profile/${supplierId}/invite`,
  cancelSupplierInvite: (supplierId) =>
    `community/supplier-profile/${supplierId}/cancel-invite`,
  acceptSupplierOffer: (supplierId) =>
    `community/supplier-profile/${supplierId}/accept-offer`,
  declineSupplierOffer: (supplierId) =>
    `community/supplier-profile/${supplierId}/decline-offer`,
  updateExistingContract: (supplierId) =>
    `community/supplier-profile/${supplierId}/update-contract`,
  pauseSupplier: (supplierId) =>
    `community/supplier-profile/${supplierId}/pause`,
  activateSupplier: (supplierId) =>
    `community/supplier-profile/${supplierId}/activate`,
  removeSupplierProfile: (supplierId) =>
    `community/supplier-profile/${supplierId}/remove`,
  // Orders still running for this supplier in the CM's community. Removal is
  // blocked while any are open — the CM can only pause until they close.
  supplierOpenOrders: (supplierId) =>
    `community/supplier-profile/${supplierId}/open-orders`,

  // Pricing endpoints
  supplierPricelists: "pricing/supplier/pricelists",
  // COMY1-292 — flat services+variants for the Edit-Order editor (single call),
  // scoped to the order's category so an edit can't re-label the order.
  supplierServicesForEditor: (categoryId) =>
    `pricing/supplier/services-for-editor${
      categoryId != null ? `?categoryId=${categoryId}` : ""
    }`,
  searchPricelist: "pricing/supplier/search-pricelist",
  getPricelistDetails: (subcategoryId) =>
    `pricing/supplier/pricelist/${subcategoryId}`,
  deletePricelist: (subcategoryId) =>
    `pricing/supplier/pricelist/${subcategoryId}`,
  savePricelist: (subcategoryId) =>
    `pricing/supplier/pricelist/${subcategoryId}/save`,
  getActiveOrdersCount: (subcategoryId) =>
    `pricing/supplier/pricelist/${subcategoryId}/active-orders`,
  checkPriceChanges: (subcategoryId) =>
    `pricing/supplier/pricelist/${subcategoryId}/check-price-changes`,
  checkRemovedVariants: (subcategoryId) =>
    `pricing/supplier/pricelist/${subcategoryId}/check-removed-variants`,
  getPricingCategories: "pricing/categories",
  getPricingSubcategories: "pricing/supplier/subcategories",
  submitMissingServiceRequest: "pricing/missing-service-request",
  uploadPricelistFile: (subcategoryId) =>
    `pricing/supplier/${subcategoryId}/files/upload`,
  removePricelistFile: (subcategoryId) =>
    `pricing/supplier/${subcategoryId}/files/remove`,
  getCommunitySupplierPricelistDetails: "pricing/supplier/pricelist-details",
  compareVariantPrice: "pricing/compare-variant-price",
  missingCategoryRequest: "pricing/missing-category",

  // Product endpoints
  supplierProducts: "products/supplier",
  createProduct: "products/supplier",
  updateProduct: (productId) => `products/supplier/${productId}`,
  deleteProduct: (productId) => `products/supplier/${productId}`,
  updateProductStatus: (productId) => `products/supplier/${productId}/status`,
  getProductCategories: "products/categories",
  getAllProductTags: "products/tags",
  missingProductCategory: "products/missing-category",

  // Catalog endpoints per vertical. The BE mounts the same router at
  // /api/products and /api/restaurants, so stores and restaurants expose an
  // identical surface — the only difference is this path segment. Use this
  // instead of the hardcoded product endpoints above for anything that has to
  // work for both.
  catalog: (vertical = "products") => ({
    supplierItems: `${vertical}/supplier`,
    createItem: `${vertical}/supplier`,
    updateItem: (itemId) => `${vertical}/supplier/${itemId}`,
    deleteItem: (itemId) => `${vertical}/supplier/${itemId}`,
    updateItemStatus: (itemId) => `${vertical}/supplier/${itemId}/status`,
    getCategories: `${vertical}/categories`,
    getAllTags: `${vertical}/tags`,
    missingCategory: `${vertical}/missing-category`,
  }),

  // sidebar
  getSidebarData: "sidebar",
  getAllLanguages: "sidebar/get-all-languages",
  updateLanguage: "sidebar/language",
  getStartedTasks: "sidebar/getStartedTasks",

  // role view
  toggleRoleByPageView: "role-view/toggle-role-by-pageview",
  toggleAdminSupplierRole: "role-view/toggle-admin-supplier",

  // profile
  getProfileData: "profile",
  updateProfileData: "profile",
  deleteAccount: "profile",
  updateProfileImage: "profile/image",
  getOpenOrders: "profile/open-orders",

  //banks
  getAllBanks: "banks/get_all",
  getBankBranches: (bankId) => `banks/${bankId}/branches`,

  //supplier business settings
  getSupplierSettings: "supplier/business-settings",
  updateSupplierProfile: "supplier/business-settings/profile",
  updateSupplierOpeningHours: "supplier/business-settings/opening-hours",
  updateSupplierStoreSettings: "supplier/business-settings/store",
  updateSupplierRestaurantSettings: "supplier/business-settings/restaurant",
  updateSupplierBankAccount: "supplier/business-settings/bank",
  updateSupplierBusinessImg: "supplier/business-settings/image",
  updateSupplierBusinessImgByType: (type) =>
    `supplier/business-settings/image?type=${type}`,

  //supplier community management
  getSupplierOffers: (id) => `supplier/community-management/${id}/offer`,
  getCommunityTOS: (id) => `supplier/community-management/${id}/tos`,
  updateSupplierOffer: (id) =>
    `supplier/community-management/${id}/update-offer`,
  acceptSupplierJoinRequest: (id) =>
    `supplier/community-management/join-requests/${id}/accept`,
  communityManagementActiveOrders: (id) =>
    `supplier/community-management/${id}/active-orders`,
  communityManagementActivateCommunity: (id) =>
    `supplier/community-management/${id}/activate`,

  // community-manager dashboard — the whole page in one call (COMY1-350).
  // The community comes from the signed-in manager's profile, so no params.
  getCommunityDashboard: "community/dashboard",

  //community members
  getCommunityMembersStats: "community/members/stats",

  // supplier dashboard — the whole page in one call (TBBR-61). The signed-in
  // supplier comes from the auth cookie, so there is nothing to pass.
  getSupplierDashboard: "supplier/dashboard",

  // supplier jobs
  getSupplierJobsStats: (profileId) =>
    `supplier/jobs/stats?profileId=${profileId}`,
  getSupplierJobsData: (params) => {
    const { type, profileId, pageNumber, pageSize, sortBy, sortOrder, itemType } =
      params;
    const baseUrl = `supplier/jobs/${type}?`;

    const queryParams = [
      `profileId=${profileId}`,
      `page=${pageNumber}`,
      `pageSize=${pageSize}`,
      sortBy && `sortBy=${sortBy}`,
      sortOrder && `sortOrder=${sortOrder}`,
      // Catalog orders share the jobs endpoints; `type=product` /
      // `type=restaurant` switches the backend to that vertical's order tables
      // (COMY1-283). Anything else means service jobs, which send no type.
      ["product", "restaurant"].includes(itemType) && `type=${itemType}`,
    ].filter(Boolean);

    return baseUrl + queryParams.join("&");
  },
  // `type` switches the backend to that vertical's order tables; omitting it
  // means a service job. A catalog job MUST send it — without the type the BE
  // looks the id up in the wrong table and 404s.
  getSupplierJobDetails: (jobId, profileId, itemType) =>
    `supplier/jobs/${jobId}/details?profileId=${profileId}${
      ["product", "restaurant"].includes(itemType) ? `&type=${itemType}` : ""
    }`,
  // The order-status vocabulary (id + name) behind the status UI. The client
  // asks for it instead of carrying its own copy of the ids — COMY1-283.
  // Omitting `itemType` returns the service-order statuses.
  getOrderStatuses: (itemType) =>
    `supplier/jobs/order-statuses${
      ["product", "restaurant"].includes(itemType) ? `?type=${itemType}` : ""
    }`,
  // Supplier sets a product order's status (status dropdown) — COMY1-283.
  updateProductJobStatus: (jobId, profileId) =>
    `supplier/jobs/${jobId}/product-status?profileId=${profileId}`,
  // Supplier returns a product order to the member for re-approval after
  // un-checking unavailable items — COMY1-283.
  returnProductJobForReapproval: (jobId, profileId) =>
    `supplier/jobs/${jobId}/product-return?profileId=${profileId}`,
  // Supplier edits a service order (services/variants/products), which moves the
  // order back to member re-approval and notifies the client — COMY1-292.
  updateSupplierJobOrder: (jobId, profileId) =>
    `supplier/jobs/${jobId}/update?profileId=${profileId}`,
  supplierJobActions: (jobId, profileId, type) =>
    `supplier/jobs/${jobId}/${type}?profileId=${profileId}`,
  updateOnlineJobMeetingLink: (jobId, profileId) =>
    `supplier/jobs/${jobId}/online-link?profileId=${profileId}`,
  hasSupplierBankDetails: (profileId) =>
    `supplier/jobs/bank/check?profileId=${profileId}`,

  // Moderator actions on members
  promoteMemberToModerator: (memberId) =>
    `community/members/${memberId}/promote-moderator`,
  demoteModerator: (memberId) =>
    `community/members/${memberId}/demote-moderator`,

  // Notifications
  getNotificationContactInfo: ({
    contactType,
    communityId,
    communityName,
    supplierProfileId,
    supplierName,
  }) => {
    const params = new URLSearchParams({ contactType });
    if (communityId != null) params.append("communityId", communityId);
    if (communityName) params.append("communityName", communityName);
    if (supplierProfileId != null)
      params.append("supplierProfileId", supplierProfileId);
    if (supplierName) params.append("supplierName", supplierName);
    return `notifications/contact-info?${params.toString()}`;
  },

  // User community management
  getUserCommunities: "community/user/communities",
  switchCommunity: "community/user/switch",
};

export default endpoints;
